import React, {useState, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Tabs,
  Tab,
  Form,
  Table,
  ProgressBar,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  FaChartLine,
  FaDownload,
  FaUsers,
  FaPoll,
  FaTrophy,
  FaExclamationTriangle,
  FaInfoCircle,
  FaChartBar,
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {Bar, Line, Doughnut} from "react-chartjs-2";
import * as XLSX from "xlsx";
import RealTimeMetrics from "../components/RealTimeMetrics";

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Datos mock para analytics
const generateMockAnalytics = (campaigns, contacts, lists) => {
  const analytics = {
    overview: {
      totalCampaigns: campaigns.length,
      totalMessages: campaigns.reduce(
        (acc, c) => acc + (c.comunicados?.length || 1),
        0
      ),
      totalRecipients: new Set(
        campaigns.flatMap((c) =>
          (c.sentToListIds || []).flatMap(
            (listId) => lists.find((l) => l.id === listId)?.members || []
          )
        )
      ).size,
      deliveryRate: 98.5,
      readRate: 85.2,
      responseRate: 21.4,
      avgResponseTime: "2.3 horas",
    },
    campaignPerformance: campaigns.map((campaign) => ({
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      sentDate: campaign.sentDate,
      recipients: (campaign.sentToListIds || []).reduce((acc, listId) => {
        const list = lists.find((l) => l.id === listId);
        return acc + (list?.members.length || 0);
      }, 0),
      delivered: Math.floor(Math.random() * 100 + 900),
      read: Math.floor(Math.random() * 80 + 750),
      responded: Math.floor(Math.random() * 30 + 150),
      failed: Math.floor(Math.random() * 10 + 5),
      deliveryRate: (95 + Math.random() * 5).toFixed(1),
      readRate: (80 + Math.random() * 15).toFixed(1),
      responseRate: (15 + Math.random() * 15).toFixed(1),
      avgResponseTime: `${(1 + Math.random() * 4).toFixed(1)} horas`,
      topResponses: [
        {text: "Sí, confirmo", count: Math.floor(Math.random() * 50 + 20)},
        {text: "No puedo", count: Math.floor(Math.random() * 30 + 10)},
        {text: "Tal vez", count: Math.floor(Math.random() * 20 + 5)},
      ],
    })),
    timeAnalysis: {
      hourly: Array.from({length: 24}, (_, i) => ({
        hour: i,
        sent: Math.floor(Math.random() * 100 + 50),
        read: Math.floor(Math.random() * 80 + 40),
        responded: Math.floor(Math.random() * 20 + 5),
      })),
      daily: Array.from({length: 7}, (_, i) => ({
        day: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"][i],
        sent: Math.floor(Math.random() * 200 + 100),
        read: Math.floor(Math.random() * 160 + 80),
        responded: Math.floor(Math.random() * 40 + 20),
      })),
    },
    audienceInsights: lists.map((list) => ({
      id: list.id,
      name: list.name,
      members: list.members.length,
      engagement: (70 + Math.random() * 25).toFixed(1),
      avgResponseTime: `${(1 + Math.random() * 3).toFixed(1)} horas`,
      topOrganizations: [
        ...new Set(
          list.members
            .map((memberId) => {
              const contact = contacts.find((c) => c.id === memberId);
              return contact?.transportista;
            })
            .filter(Boolean)
        ),
      ].slice(0, 3),
    })),
    pollResults: campaigns
      .filter((c) => c.comunicados?.some((com) => com.poll) || c.poll)
      .map((campaign) => {
        const polls =
          campaign.comunicados?.filter((com) => com.poll) ||
          (campaign.poll ? [{poll: campaign.poll, title: campaign.name}] : []);

        return polls.map((pollData) => ({
          campaignId: campaign.id,
          campaignName: campaign.name,
          pollTitle: pollData.title || "Encuesta Principal",
          question: pollData.poll.question,
          totalResponses: Math.floor(Math.random() * 200 + 100),
          responses: pollData.poll.options.map((option) => ({
            option,
            count: Math.floor(Math.random() * 80 + 20),
            percentage: (Math.random() * 40 + 10).toFixed(1),
          })),
        }));
      })
      .flat(),
  };

  return analytics;
};

const Analytics = ({campaigns = [], contacts = [], lists = []}) => {
  const [dateRange, setDateRange] = useState("30");
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Generar datos de analytics
  const analytics = useMemo(
    () => generateMockAnalytics(campaigns, contacts, lists),
    [campaigns, contacts, lists]
  );

  // Configuración de gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.1)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Datos para gráfico de rendimiento por campaña
  const campaignChartData = {
    labels: analytics.campaignPerformance
      .slice(0, 5)
      .map((c) => c.name.substring(0, 15) + "..."),
    datasets: [
      {
        label: "Entregados",
        data: analytics.campaignPerformance.slice(0, 5).map((c) => c.delivered),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
        borderRadius: 4,
      },
      {
        label: "Leídos",
        data: analytics.campaignPerformance.slice(0, 5).map((c) => c.read),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderRadius: 4,
      },
      {
        label: "Respondidos",
        data: analytics.campaignPerformance.slice(0, 5).map((c) => c.responded),
        backgroundColor: "rgba(249, 115, 22, 0.8)",
        borderRadius: 4,
      },
    ],
  };

  // Datos para gráfico de tiempo
  const timeChartData = {
    labels: analytics.timeAnalysis.daily.map((d) => d.day),
    datasets: [
      {
        label: "Enviados",
        data: analytics.timeAnalysis.daily.map((d) => d.sent),
        borderColor: "rgb(34, 197, 94)",
        backgroundColor: "rgba(34, 197, 94, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Leídos",
        data: analytics.timeAnalysis.daily.map((d) => d.read),
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Datos para gráfico de canales
  const channelData = {
    labels: ["WhatsApp", "SMS", "Email"],
    datasets: [
      {
        data: [85, 10, 5],
        backgroundColor: [
          "rgba(37, 211, 102, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(239, 68, 68, 0.8)",
        ],
        borderWidth: 0,
      },
    ],
  };

  const handleExport = (type) => {
    let data = [];
    let filename = "";

    switch (type) {
      case "campaigns":
        data = analytics.campaignPerformance.map((c) => ({
          Campaña: c.name,
          Estado: c.status,
          Destinatarios: c.recipients,
          Entregados: c.delivered,
          Leídos: c.read,
          Respondidos: c.responded,
          "Tasa Entrega": c.deliveryRate + "%",
          "Tasa Lectura": c.readRate + "%",
          "Tasa Respuesta": c.responseRate + "%",
        }));
        filename = "Reporte_Campañas.xlsx";
        break;
      case "polls":
        data = analytics.pollResults.flatMap((poll) =>
          poll.responses.map((r) => ({
            Campaña: poll.campaignName,
            Pregunta: poll.question,
            Opción: r.option,
            Respuestas: r.count,
            Porcentaje: r.percentage + "%",
          }))
        );
        filename = "Resultados_Encuestas.xlsx";
        break;
      case "audiences":
        data = analytics.audienceInsights.map((a) => ({
          Audiencia: a.name,
          Miembros: a.members,
          Engagement: a.engagement + "%",
          "Tiempo Respuesta": a.avgResponseTime,
          "Top Organizaciones": a.topOrganizations.join(", "),
        }));
        filename = "Analisis_Audiencias.xlsx";
        break;
      default:
        return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");
    XLSX.writeFile(workbook, filename);
    setShowExportModal(false);
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold mb-1">
                <FaChartLine className="me-3 text-primary" />
                Analytics Avanzado
              </h2>
              <p className="text-muted mb-0">
                Análisis detallado del rendimiento de tus comunicaciones
              </p>
            </div>
            <div className="d-flex gap-2">
              <Form.Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{width: "auto"}}
              >
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 3 meses</option>
                <option value="365">Último año</option>
              </Form.Select>
              <Button
                variant="outline-primary"
                onClick={() => setShowExportModal(true)}
              >
                <FaDownload className="me-2" />
                Exportar
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Tabs de análisis */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
        fill
      >
        {/* Tab Overview */}
        <Tab eventKey="overview" title="Resumen General">
          <Row>
            <Col lg={8}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <h6 className="fw-bold mb-0">Rendimiento por Campaña</h6>
                </Card.Header>
                <Card.Body style={{height: "400px"}}>
                  <Bar data={campaignChartData} options={chartOptions} />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <h6 className="fw-bold mb-0">Distribución por Canal</h6>
                </Card.Header>
                <Card.Body style={{height: "400px"}}>
                  <Doughnut
                    data={channelData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                        },
                      },
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white">
                  <h6 className="fw-bold mb-0">Tendencia Semanal</h6>
                </Card.Header>
                <Card.Body style={{height: "300px"}}>
                  <Line data={timeChartData} options={chartOptions} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Tab Campañas */}
        <Tab eventKey="campaigns" title="Análisis de Campañas">
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0">Detalle por Campaña</h6>
              <Form.Select
                style={{width: "200px"}}
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                <option value="all">Todas las campañas</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Campaña</th>
                    <th>Estado</th>
                    <th>Destinatarios</th>
                    <th>Entregados</th>
                    <th>Leídos</th>
                    <th>Respondidos</th>
                    <th>Tasa Lectura</th>
                    <th>Tasa Respuesta</th>
                    <th>Tiempo Respuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.campaignPerformance
                    .filter(
                      (c) =>
                        selectedCampaign === "all" ||
                        c.id.toString() === selectedCampaign
                    )
                    .map((campaign) => (
                      <tr key={campaign.id}>
                        <td className="fw-bold">{campaign.name}</td>
                        <td>{campaign.recipients}</td>
                        <td>{campaign.delivered}</td>
                        <td>{campaign.read}</td>
                        <td>{campaign.responded}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <ProgressBar
                              now={parseFloat(campaign.readRate)}
                              style={{width: "60px", height: "8px"}}
                              className="me-2"
                            />
                            {campaign.readRate}%
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <ProgressBar
                              now={parseFloat(campaign.responseRate)}
                              variant="warning"
                              style={{width: "60px", height: "8px"}}
                              className="me-2"
                            />
                            {campaign.responseRate}%
                          </div>
                        </td>
                        <td>{campaign.avgResponseTime}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* Tab Encuestas */}
        <Tab eventKey="polls" title="Resultados de Encuestas">
          {analytics.pollResults.length > 0 ? (
            <Row>
              {analytics.pollResults.map((poll, index) => (
                <Col lg={6} key={index} className="mb-4">
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white">
                      <h6 className="fw-bold mb-1">{poll.pollTitle}</h6>
                      <small className="text-muted">{poll.campaignName}</small>
                    </Card.Header>
                    <Card.Body>
                      <p className="fw-bold text-primary mb-3">
                        {poll.question}
                      </p>
                      <div className="mb-3">
                        <FaUsers className="me-1" />
                      </div>
                      {poll.responses.map((response, idx) => (
                        <div key={idx} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="small fw-bold">
                              {response.option}
                            </span>
                            <span className="small text-muted">
                              {response.count} ({response.percentage}%)
                            </span>
                          </div>
                          <ProgressBar
                            now={parseFloat(response.percentage)}
                            style={{height: "8px"}}
                            variant={
                              idx === 0
                                ? "success"
                                : idx === 1
                                ? "primary"
                                : "warning"
                            }
                          />
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Alert variant="info">
              <FaInfoCircle className="me-2" />
              No hay encuestas disponibles en las campañas actuales.
            </Alert>
          )}
        </Tab>

        {/* Tab Audiencias */}
        <Tab eventKey="audiences" title="Análisis de Audiencias">
          <Row>
            {analytics.audienceInsights.map((audience) => (
              <Col lg={4} key={audience.id} className="mb-4">
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white">
                    <h6 className="fw-bold mb-0">{audience.name}</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="row text-center mb-3">
                      <div className="col">
                        <div className="fw-bold fs-4 text-primary">
                          {audience.members}
                        </div>
                        <div className="small text-muted">Miembros</div>
                      </div>
                      <div className="col">
                        <div className="fw-bold fs-4 text-success">
                          {audience.engagement}%
                        </div>
                        <div className="small text-muted">Engagement</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">
                        Tiempo promedio de respuesta:
                      </small>
                      <div className="fw-bold">{audience.avgResponseTime}</div>
                    </div>
                    <div>
                      <small className="text-muted">Top Organizaciones:</small>
                      <div className="mt-1"></div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Tab>

        {/* Tab Tiempo Real */}
        <Tab eventKey="realtime" title="Tiempo Real">
          <RealTimeMetrics campaigns={campaigns} isLive={true} />
        </Tab>

        {/* Tab Tiempo */}
        <Tab eventKey="timing" title="Análisis Temporal">
          <Row>
            <Col lg={6}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <h6 className="fw-bold mb-0">Actividad por Hora del Día</h6>
                </Card.Header>
                <Card.Body style={{height: "300px"}}>
                  <Bar
                    data={{
                      labels: analytics.timeAnalysis.hourly.map(
                        (h) => `${h.hour}:00`
                      ),
                      datasets: [
                        {
                          label: "Mensajes Leídos",
                          data: analytics.timeAnalysis.hourly.map(
                            (h) => h.read
                          ),
                          backgroundColor: "rgba(59, 130, 246, 0.8)",
                          borderRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      ...chartOptions,
                      scales: {
                        ...chartOptions.scales,
                        x: {
                          ...chartOptions.scales.x,
                          ticks: {
                            maxTicksLimit: 12,
                          },
                        },
                      },
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <h6 className="fw-bold mb-0">Mejores Horarios para Envío</h6>
                </Card.Header>
                <Card.Body>
                  <Alert variant="success" className="mb-3">
                    <FaTrophy className="me-2" />
                    <strong>Horario Óptimo:</strong> 10:00 - 12:00 hrs
                  </Alert>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Mañana (8:00 - 12:00)</span>
                      <span className="fw-bold text-success">92% lectura</span>
                    </div>
                    <ProgressBar
                      now={92}
                      variant="success"
                      style={{height: "8px"}}
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Tarde (12:00 - 18:00)</span>
                      <span className="fw-bold text-primary">78% lectura</span>
                    </div>
                    <ProgressBar
                      now={78}
                      variant="primary"
                      style={{height: "8px"}}
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Noche (18:00 - 22:00)</span>
                      <span className="fw-bold text-warning">65% lectura</span>
                    </div>
                    <ProgressBar
                      now={65}
                      variant="warning"
                      style={{height: "8px"}}
                    />
                  </div>
                  <Alert variant="warning" className="mt-3">
                    <FaExclamationTriangle className="me-2" />
                    Evita envíos después de las 22:00 hrs
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Modal de Exportación */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Exportar Datos de Analytics</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Selecciona qué datos deseas exportar:</p>
          <div className="d-grid gap-2">
            <Button
              variant="outline-primary"
              onClick={() => handleExport("campaigns")}
            >
              <FaChartBar className="me-2" />
              Reporte de Campañas
            </Button>
            <Button
              variant="outline-success"
              onClick={() => handleExport("polls")}
            >
              <FaPoll className="me-2" />
              Resultados de Encuestas
            </Button>
            <Button
              variant="outline-info"
              onClick={() => handleExport("audiences")}
            >
              <FaUsers className="me-2" />
              Análisis de Audiencias
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Analytics;
